import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MyMealsStickyFooter from '../Common/MyMealsStickyFooter';
import TriesLeftDrawer from '../PremiumPromo/TriesLeftDrawer';
import ReplaceMealPlanDrawer from '../MealPlan/ReplaceMealPlanDrawer';
import MealPlanDetailsDrawer from './Drawers/MealPlanDetailsDrawer';
import WhyNineMealsDrawer from '../UserWizard/Drawers/WhyNineMealsDrawer';
import RecipeCard from '../Recipes/RecipeCard';
import { getUserCountry } from '../../services/apiService';
import { getCountryCurrency } from '../../services/countryService';
import { useMealPlan } from '../../contexts/MealPlanContext';
import { computeAdjustedTotalFromProducts } from '../MealPlan/utils/mealPlanHelpers';
import { useUser } from '../../contexts/UserContext';
import backendClient from '../../services/backendClient';
import { getFingerprint } from '../../utils/fingerprint';
import { trackMyMealsViewed, trackSavedMealSetOpened } from '../../analytics';
import { useFavoriteMeals } from '../../hooks/useFavoriteMeals';
import { useCookingStates } from '../../hooks/useCookingStates';

const MyMealPlans = ({
  mealPlan,
  productList,
  totalCost,
  maxStorageTime,
  imagesLoading,
  leftovers,
  triesLeft = 3,
  onTriesUpdate,
  initialShoppingModeState, // Prop from parent with loaded data
  initialHaveItState, // Prop from parent with loaded "I have it" data
  onStartNewMealPlan, // Prop from parent to navigate to wizard
  savedCookingStyle // Cooking style saved with the meal plan (new format)
}) => {
  const { t } = useTranslation('mealplan');
  const { isMealFavorite, toggleFavoriteMeal } = useFavoriteMeals();
  
  // Create rotating descriptions from translations
  const ROTATING_DESCRIPTIONS = [
    t('tips.buyGroceries'),
    t('tips.startCooking'),
    t('tips.markComplete')
  ];
  
  const navigate = useNavigate();
  const [startOverDrawerOpen, setStartOverDrawerOpen] = useState(false);
  const [startOverConfirmDrawerOpen, setStartOverConfirmDrawerOpen] = useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [whyNineMealsDrawerOpen, setWhyNineMealsDrawerOpen] = useState(false);
  const { cookingStates, startCooking, completeCooking } = useCookingStates();
  const [userCountry, setUserCountry] = useState(null);
  const [currentDescriptionIndex, setCurrentDescriptionIndex] = useState(0);
  const [activeMealTab, setActiveMealTab] = useState(null); // For mobile meal type tabs
  const [isPremium, setIsPremium] = useState(false); // Premium status
  const [isAdmin, setIsAdmin] = useState(false);
  const [promoLoadingByRecipeKey, setPromoLoadingByRecipeKey] = useState({});
  const [promoAddedByRecipeKey, setPromoAddedByRecipeKey] = useState({});
  const [promoToast, setPromoToast] = useState({ visible: false, message: '', kind: 'success' });
  const hasInitialized = useRef(false); // Prevent auto-save on initial mount
  const hasHaveItInitialized = useRef(false); // Prevent auto-save haveItState on initial mount

  const showPromoToast = (message, kind = 'success') => {
    setPromoToast({ visible: true, message, kind });
  };

  useEffect(() => {
    if (!promoToast.visible) return undefined;
    const timer = setTimeout(() => {
      setPromoToast((prev) => ({ ...prev, visible: false }));
    }, 2400);
    return () => clearTimeout(timer);
  }, [promoToast.visible]);
  
  // Get shoppingModeState and haveItState from context for persistence
  const { shoppingModeState, setShoppingModeState, haveItState, setHaveItState } = useMealPlan();
  
  // Get user preferences from context
  const { numberOfPeople, budget, selectedCuisine, dietaryPreferences } = useUser();

  // Compute adjusted cost that automatically stays in sync with productList and haveItState
  const displayTotalCost = useMemo(() => {
    const adjusted = computeAdjustedTotalFromProducts(productList, haveItState);
    if (adjusted === null) return totalCost;
    const currencySymbol = userCountry ? getCountryCurrency(userCountry).symbol : '€';
    return `${currencySymbol}${adjusted.toFixed(2)}`;
  }, [productList, haveItState, totalCost, userCountry]);

  // Initialize context with loaded data on mount
  useEffect(() => {
    if (initialShoppingModeState && Object.keys(initialShoppingModeState).length > 0) {
      setShoppingModeState(initialShoppingModeState);
    }
  }, [initialShoppingModeState, setShoppingModeState]);

  // Initialize haveItState from props on mount
  useEffect(() => {
    if (initialHaveItState && Object.keys(initialHaveItState).length > 0) {
      setHaveItState(initialHaveItState);
    }
  }, [initialHaveItState, setHaveItState]);

  // Debug: Log when component mounts/unmounts
  useEffect(() => {
    return () => {
      console.log('💀 MyMealPlans component UNMOUNTING');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPremiumStatus = async () => {
      try {
        const params = new URLSearchParams();
        const authToken = localStorage.getItem("authToken");
        if (!authToken) {
          const fp = getFingerprint();
          if (fp) params.set("fingerprint", fp);
        }
        const qs = params.toString();
        const data = await backendClient.get(`/api/meal-plan-limit/check${qs ? `?${qs}` : ""}`);
        if (data.isPremium !== undefined) {
          setIsPremium(data.isPremium);
        }
        if (data.isAdmin !== undefined) {
          setIsAdmin(data.isAdmin);
        }
      } catch (error) {
        // Silently fail - default to non-premium behavior
      }
    };

  // Fetch premium/admin status on mount and when window regains focus
  useEffect(() => {
    fetchPremiumStatus();
    const onFocus = () => fetchPremiumStatus();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchPremiumStatus();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Keep isPremium accessible synchronously inside async handlers
  const isPremiumRef = useRef(isPremium);
  useEffect(() => { isPremiumRef.current = isPremium; }, [isPremium]);

  // Lazy-resolve premium status on demand. The fetchPremiumStatus effect above
  // may not have completed by the time the user clicks "Create new meals" in
  // MyMealsStickyFooter — without this, premium users get the wrong drawer
  // (TriesLeftDrawer instead of ReplaceMealPlanDrawer). Mirrors the pattern
  // used in App.js for the back/logo handlers.
  const resolvePremiumStatus = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return false;
    try {
      const fp = getFingerprint();
      const qs = fp ? `?fingerprint=${encodeURIComponent(fp)}` : '';
      const data = await backendClient.get(`/api/meal-plan-limit/check${qs}`);
      if (data?.isPremium !== undefined) {
        setIsPremium(!!data.isPremium);
        return !!data.isPremium;
      }
    } catch (e) {
      // Non-critical — fall back to current ref value
    }
    return isPremiumRef.current;
  };

  // Unified "Create new meals" handler. Passed to BOTH onStartNewMealPlan
  // and onOpenStartOverDrawer so the footer's stale `isPremium` branch is
  // effectively bypassed — we lazily resolve premium and pick the drawer here.
  const handleCreateNewClick = async () => {
    let currentPremium = isPremiumRef.current;
    if (!currentPremium && localStorage.getItem('authToken')) {
      currentPremium = await resolvePremiumStatus();
    }
    if (currentPremium) {
      setStartOverConfirmDrawerOpen(true);
    } else {
      setStartOverDrawerOpen(true);
    }
  };

  // Debug: Log when mealPlan changes
  useEffect(() => {
    console.log('🔄 mealPlan prop changed! New length:', mealPlan?.length);
    console.log('🔄 Current cookingStates:', cookingStates);
    // Debug: Log all recipes with their cookingTime
    if (mealPlan?.length > 0) {
      console.log('🍳 All recipes cookingTime values:');
      mealPlan.forEach((recipe, idx) => {
        console.log(`  [${idx}] ${recipe.title}: cookingTime = "${recipe.cookingTime}"`);
      });
    }
  }, [mealPlan, cookingStates]);

  // Debug: Log cookingStates changes
  useEffect(() => {
    console.log('🔧 cookingStates UPDATED:', cookingStates);
  }, [cookingStates]);

  // Track My Meals Viewed on component mount
  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (!hasTrackedView.current && mealPlan?.length > 0) {
      hasTrackedView.current = true;
      
      // Track My Meals page view
      trackMyMealsViewed({
        saved_plans_count: 1, // This component shows one meal plan at a time
        days_since_last_plan: null, // Could calculate this if we have createdAt
      });
      
      // Track that user opened their saved meal set
      trackSavedMealSetOpened({
        meal_set_id: null, // Could use meal plan id if available
        plan_age_days: null, // Could calculate from createdAt
        entry_source: 'my_meals',
      });
    }
  }, [mealPlan]);

  // Fetch user country
  useEffect(() => {
    const fetchUserCountry = async () => {
      try {
        const country = await getUserCountry();
        setUserCountry(country);
      } catch (error) {
        console.error('Error fetching user country:', error);
      }
    };
    fetchUserCountry();
  }, []);

  // Rotate description every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDescriptionIndex(
        (prev) => (prev + 1) % ROTATING_DESCRIPTIONS.length
      );
    }, 8000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save shoppingModeState changes to backend
  useEffect(() => {
    // Skip auto-save on initial mount to prevent overwriting DB with empty state
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      return;
    }
    
    const saveShoppingModeState = async () => {
      // Don't save if we don't have a meal plan or shoppingModeState is empty
      if (!mealPlan || mealPlan.length === 0 || !shoppingModeState || Object.keys(shoppingModeState).length === 0) {
        return;
      }
      
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          return;
        }

        const mealPlanData = {
          mealPlan,
          groceryList: {
            products: productList,
            totalCost,
            maxStorageTime,
            shoppingModeState: shoppingModeState || {},
            leftovers: leftovers || [],
          },
        };

        await backendClient.post('/api/mealplan/save', mealPlanData, { timeout: 30000 });
      } catch (error) {
        console.error('Error saving shoppingModeState:', error);
      }
    };

    // Debounce the save to avoid too many requests
    const timeoutId = setTimeout(saveShoppingModeState, 500);
    return () => clearTimeout(timeoutId);
  }, [shoppingModeState, mealPlan, productList, totalCost, maxStorageTime, leftovers]);

  // Auto-save haveItState changes to backend
  useEffect(() => {
    // Skip auto-save on initial mount to prevent overwriting DB with empty state
    if (!hasHaveItInitialized.current) {
      hasHaveItInitialized.current = true;
      return;
    }
    
    const saveHaveItState = async () => {
      // Don't save if we don't have a meal plan
      if (!mealPlan || mealPlan.length === 0) {
        return;
      }
      
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          return;
        }

        const mealPlanData = {
          mealPlan,
          groceryList: {
            products: productList,
            totalCost,
            maxStorageTime,
            haveItState: haveItState || {},
            shoppingModeState: shoppingModeState || {},
            leftovers: leftovers || [],
          },
        };

        await backendClient.post('/api/mealplan/save', mealPlanData, { timeout: 30000 });
        console.log('✅ Saved haveItState to backend:', haveItState);
      } catch (error) {
        console.error('Error saving haveItState:', error);
      }
    };

    // Debounce the save to avoid too many requests
    const timeoutId = setTimeout(saveHaveItState, 500);
    return () => clearTimeout(timeoutId);
  }, [haveItState, mealPlan, productList, totalCost, maxStorageTime, leftovers, shoppingModeState]);


  // Card body opens recipe details; cooking action buttons open cooking mode.
  const getRecipeIndex = (recipe) => {
    const key = recipe?.id || recipe?.title;
    return mealPlan.findIndex((r) => (r.id || r.title) === key);
  };

  const openCookingMode = async (recipe) => {
    const index = getRecipeIndex(recipe);
    if (index < 0) return;

    const recipeId = recipe?.id || recipe?.title;
    const cookingState = cookingStates[recipeId];
    if (recipeId && !cookingState?.startedAt && !cookingState?.isCompleted) {
      await startCooking(recipeId);
    }

    navigate(`/mymeals/recipe/${index}/cook`);
  };

  const handleRecipeClick = (recipe) => {
    const index = getRecipeIndex(recipe);
    if (index >= 0) {
      navigate(`/mymeals/recipe/${index}`);
    }
  };

  // Delete meal plan and start from scratch
  const handleStartOver = async () => {
    try {
      await backendClient.delete('/api/mealplan');
      // Navigate to wizard instead of reloading
      if (onStartNewMealPlan) {
        onStartNewMealPlan();
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to delete meal plan:', error);
    }
  };

  // Card "Start cooking" → mark started and jump into cooking mode.
  const handleStartCooking = async (recipeId) => {
    const recipe = mealPlan.find((r) => (r.id || r.title) === recipeId);
    if (recipe) {
      await openCookingMode(recipe);
    }
  };

  // Card "Complete" → mark recipe cooked.
  const handleCompleteCooking = async (recipeId) => {
    await completeCooking(recipeId);
  };

  const handleAddToPromo = async (recipe) => {
    if (!recipe || !isAdmin) return;

    const recipeKey = recipe.id || recipe.originalTitle || recipe.title;
    if (!recipeKey) return;

    try {
      setPromoLoadingByRecipeKey((prev) => ({ ...prev, [recipeKey]: true }));
      const result = await backendClient.post('/api/user/promo-meals', { meal: recipe }, { timeout: 10000 });
      if (result?.created) {
        setPromoAddedByRecipeKey((prev) => ({ ...prev, [recipeKey]: true }));
        showPromoToast('Meal added to promo', 'success');
      } else {
        showPromoToast('Meal is already in promo', 'info');
      }
    } catch (error) {
      showPromoToast(error?.message || 'Failed to add meal to promo', 'error');
    } finally {
      setPromoLoadingByRecipeKey((prev) => ({ ...prev, [recipeKey]: false }));
    }
  };

  // Render meal card with cooking actions using RecipeCard component
  const renderMealCard = (recipe, index, fullWidth = false) => {
    const recipeId = recipe.id || recipe.title;
    const cookingState = cookingStates[recipeId];
    
    // Determine servings for days based on meal type and saved cooking style
    const mealType = recipe.mealType?.toLowerCase() || '';
    const cookFor2Days = savedCookingStyle?.[mealType]?.cookFor2Days || false;
    const servingsForDays = cookFor2Days ? 2 : 1;
    
    console.log('🍳 Rendering meal card:', {
      recipeTitle: recipe.title,
      recipeId,
      cookingState,
      allCookingStates: cookingStates,
      mealType,
      savedCookingStyle,
      servingsForDays
    });
    
    return (
      <RecipeCard
        key={recipeId || `recipe-${index}`}
        recipe={recipe}
        index={index}
        onRecipeClick={handleRecipeClick}
        imagesLoading={imagesLoading}
        isRecipeReady={true}
        isSavedRecipe={true}
        cookingState={cookingState}
        onStartCooking={handleStartCooking}
        onCompleteCooking={handleCompleteCooking}
        servingsForDays={servingsForDays}
        fullWidth={fullWidth}
        isFavorite={isMealFavorite(recipe)}
        onToggleFavorite={toggleFavoriteMeal}
        showFavoriteButton={true}
        showPromoButton={isAdmin && !promoAddedByRecipeKey[recipeId]}
        onAddToPromo={handleAddToPromo}
        isAddingToPromo={!!promoLoadingByRecipeKey[recipeId]}
      />
    );
  };

  // Render meal section (Breakfast, Lunch, Dinner)
  const renderMealSection = (mealType, meals) => {
    // Helper function to get cooking priority for sorting
    // Priority: 1 = In Progress (lowest number = first), 2 = Start Cooking, 3 = Completed
    const getCookingPriority = (recipe) => {
      const recipeId = recipe.id || recipe.title;
      const cookingState = cookingStates[recipeId];
      
      if (cookingState?.isCompleted) {
        return 3; // Completed - last
      } else if (cookingState?.startedAt && !cookingState?.isCompleted) {
        return 1; // In Progress - first
      } else {
        return 2; // Start Cooking (not started) - middle
      }
    };

    // Sort meals by cooking priority
    const sortedMeals = [...meals].sort((a, b) => {
      return getCookingPriority(a) - getCookingPriority(b);
    });

    return (
      <div className="max-w-5xl mx-auto">
        <div className="pt-3 flex mb-3 px-6 justify-between items-center">
          <h3 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-center text-[18px] font-bold leading-[32px] justify-center flex items-center">
            {mealType === 'Breakfast' && 
              <div className="flex items-center pb-0.5 justify-center w-[20px] h-[20px]">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M8.33333 1.6665V3.33317M11.6667 1.6665V3.33317M13.3333 6.6665C13.5543 6.6665 13.7663 6.7543 13.9226 6.91058C14.0789 7.06686 14.1667 7.27882 14.1667 7.49984V14.1665C14.1667 15.0506 13.8155 15.8984 13.1904 16.5235C12.5652 17.1486 11.7174 17.4998 10.8333 17.4998H5.83333C4.94928 17.4998 4.10143 17.1486 3.47631 16.5235C2.85119 15.8984 2.5 15.0506 2.5 14.1665V7.49984C2.5 7.27882 2.5878 7.06686 2.74408 6.91058C2.90036 6.7543 3.11232 6.6665 3.33333 6.6665H15C15.8841 6.6665 16.7319 7.01769 17.357 7.64281C17.9821 8.26794 18.3333 9.11578 18.3333 9.99984C18.3333 10.8839 17.9821 11.7317 17.357 12.3569C16.7319 12.982 15.8841 13.3332 15 13.3332H14.1667M5 1.6665V3.33317" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            }
            {mealType === 'Lunch' && 
              <div className="flex items-center pb-0.5 justify-center w-[20px] h-[20px]">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <g clipPath="url(#clip0_1408_6195)">
                    <path d="M13.3333 1.65967L11.4166 3.57633C10.9586 4.04366 10.702 4.67195 10.702 5.32633C10.702 5.98072 10.9586 6.60901 11.4166 7.07633L12.9166 8.57634C13.384 9.0344 14.0123 9.29098 14.6666 9.29098C15.321 9.29098 15.9493 9.0344 16.4166 8.57634L18.3333 6.65967M12.5 12.493L2.74997 2.743C2.41737 3.06888 2.15315 3.45785 1.97276 3.88713C1.79238 4.31641 1.69946 4.77736 1.69946 5.243C1.69946 5.70864 1.79238 6.1696 1.97276 6.59888C2.15315 7.02815 2.41737 7.41712 2.74997 7.743L8.83331 13.8263C9.41664 14.4097 10.5 14.4097 11.1666 13.8263L12.5 12.493ZM12.5 12.493L18.3333 18.3263M1.74997 18.1597L7.08331 12.9097M15.8333 4.15967L9.99997 9.993" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_1408_6195">
                      <rect width="20" height="20" fill="white" transform="translate(0 -0.00683594)"/>
                    </clipPath>
                  </defs>
                </svg>
              </div>
            }
            {mealType === 'Dinner' && 
              <div className="flex items-center pb-0.5 justify-center w-[20px] h-[20px]">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                  <g clipPath="url(#clip0_1408_6320)">
                    <path d="M10.0001 3.48617V2.65283M10.0001 3.48617C8.45299 3.48617 6.96925 4.10075 5.87529 5.19471C4.78133 6.28867 4.16675 7.7724 4.16675 9.3195M10.0001 3.48617C11.5472 3.48617 13.0309 4.10075 14.1249 5.19471C15.2188 6.28867 15.8334 7.7724 15.8334 9.3195M12.8334 15.4862L15.5001 13.1528C15.6588 12.9996 15.8461 12.8792 16.0513 12.7983C16.2565 12.7175 16.4757 12.6779 16.6962 12.6818C16.9168 12.6857 17.1344 12.7329 17.3366 12.8209C17.5389 12.9089 17.7219 13.0358 17.8751 13.1945C18.0283 13.3532 18.1487 13.5405 18.2296 13.7457C18.3104 13.9509 18.35 14.1701 18.3461 14.3906C18.3423 14.6112 18.295 14.8288 18.207 15.0311C18.119 15.2333 17.9921 15.4163 17.8334 15.5695L14.8334 18.3195C14.2501 18.9862 13.4167 19.3195 12.5001 19.3195H9.16675C8.25008 19.3195 7.41675 18.9862 6.83342 18.3195L5.74842 17.0995C5.6703 17.0115 5.57444 16.9411 5.46715 16.8928C5.35986 16.8446 5.24356 16.8196 5.12592 16.8195H4.16675M1.66675 12.6528H11.6667C12.1088 12.6528 12.5327 12.8284 12.8453 13.141C13.1578 13.4535 13.3334 13.8775 13.3334 14.3195C13.3334 14.7615 13.1578 15.1854 12.8453 15.498C12.5327 15.8106 12.1088 15.9862 11.6667 15.9862H10.0001M3.33341 9.3195H16.6667M4.16675 12.6528V17.6528C4.16675 17.8738 4.07895 18.0858 3.92267 18.2421C3.76639 18.3984 3.55443 18.4862 3.33341 18.4862H1.66675" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_1408_6320">
                      <rect width="20" height="20" fill="white" transform="translate(0 0.986328)"/>
                    </clipPath>
                  </defs>
                </svg>
              </div>
            }
            <span className="ml-1.5">{t(`mealTypes.${mealType.toLowerCase()}`)}</span>
          </h3>
          {/* Right side: Meal count */}
          <span className="text-[#878683] text-[12.8px] font-normal">
            {t('mealCount', { count: meals.length })}{savedCookingStyle?.[mealType.toLowerCase()]?.cookFor2Days ? ` ${t('forTwoDays')}` : ''}
          </span>
        </div>
        
        <div 
          className="flex overflow-x-auto scrollbar-hide max-w-5xl mx-auto px-6 pb-4 border-[#EBEBEB] scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-gray-100"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <div className="flex flex-nowrap gap-4">
            {sortedMeals.map((recipe, index) => renderMealCard(recipe, index))}
          </div>
        </div>
      </div>
    );
  };

  // Group meals by type
  const breakfasts = mealPlan.filter(r => r.mealType === 'Breakfast');
  const lunches = mealPlan.filter(r => r.mealType === 'Lunch');
  const dinners = mealPlan.filter(r => r.mealType === 'Dinner');

  // Get available meal tabs (only show tabs with meals)
  const availableTabs = [
    ...(breakfasts.length > 0 ? ['Breakfast'] : []),
    ...(lunches.length > 0 ? ['Lunch'] : []),
    ...(dinners.length > 0 ? ['Dinner'] : []),
  ];

  // Determine default tab based on current time
  const getDefaultMealTab = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const timeInMinutes = hours * 60 + minutes;

    // Time ranges:
    // Breakfast: 4:00 - 12:00 (240 - 720 minutes)
    // Lunch: 12:00 - 16:30 (720 - 990 minutes)  
    // Dinner: 16:30 - 4:00 (990 - 1440 + 0 - 240 minutes)

    let preferredTab;
    if (timeInMinutes >= 240 && timeInMinutes < 720) {
      preferredTab = 'Breakfast';
    } else if (timeInMinutes >= 720 && timeInMinutes < 990) {
      preferredTab = 'Lunch';
    } else {
      preferredTab = 'Dinner';
    }

    // If preferred tab is available, use it
    if (availableTabs.includes(preferredTab)) {
      return preferredTab;
    }

    // If preferred tab not available, split its time range and assign to adjacent tabs
    // Find the closest available tab
    if (preferredTab === 'Breakfast') {
      // Breakfast not available: split 4:00-12:00
      // 4:00-8:00 -> Dinner (previous), 8:00-12:00 -> Lunch (next)
      if (timeInMinutes >= 240 && timeInMinutes < 480) {
        return availableTabs.includes('Dinner') ? 'Dinner' : availableTabs[0];
      } else {
        return availableTabs.includes('Lunch') ? 'Lunch' : availableTabs[0];
      }
    } else if (preferredTab === 'Lunch') {
      // Lunch not available: split 12:00-16:30
      // 12:00-14:15 -> Breakfast (previous), 14:15-16:30 -> Dinner (next)
      if (timeInMinutes >= 720 && timeInMinutes < 855) {
        return availableTabs.includes('Breakfast') ? 'Breakfast' : availableTabs[0];
      } else {
        return availableTabs.includes('Dinner') ? 'Dinner' : availableTabs[0];
      }
    } else {
      // Dinner not available: split 16:30-4:00
      // 16:30-22:15 -> Lunch (previous), 22:15-4:00 -> Breakfast (next)
      if (timeInMinutes >= 990 || timeInMinutes < 120) {
        // Late evening to early morning
        if (timeInMinutes >= 990 && timeInMinutes < 1335) {
          return availableTabs.includes('Lunch') ? 'Lunch' : availableTabs[0];
        } else {
          return availableTabs.includes('Breakfast') ? 'Breakfast' : availableTabs[0];
        }
      }
    }

    // Fallback to first available tab
    return availableTabs[0] || 'Breakfast';
  };

  // Initialize active meal tab on mount or when available tabs change
  useEffect(() => {
    if (availableTabs.length > 0 && !activeMealTab) {
      setActiveMealTab(getDefaultMealTab());
    } else if (availableTabs.length > 0 && !availableTabs.includes(activeMealTab)) {
      // If current tab is no longer available, switch to default
      setActiveMealTab(getDefaultMealTab());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableTabs.join(',')]);

  return (
    <div className="min-h-safe-screen">
      {/* Header Section */}
      <div className="px-6 pt-7 pb-5 max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold mb-1">{t('pickMealToCook')}</h1>
        <p className="font-medium text-[#3F3C31] text-[15.2px] leading-[28px] mb-4 transition-opacity duration-500">
          {ROTATING_DESCRIPTIONS[currentDescriptionIndex]}
        </p>
        {/* Tags */}
            <div className="flex gap-2 mb-0">
              {/* Details List Button */}
             
              <span
                onClick={() => setDetailsDrawerOpen(true)}
                className={`inline-flex items-center
                    pl-2 pr-3 py-1
                    text-sm font-medium
                    rounded-[10px]
                    border-[0.5px] border-[#F3F3F2]
                    bg-[rgba(255,255,255,0.64)]
                    shadow-[4px_12px_20px_0px_rgba(83,81,69,0.08)]
                    backdrop-blur-lg
                    cursor-pointer
                    md:hover:bg-[rgba(255,255,255,0.8)]
                    transition-colors`}
              >
                <span className="mr-1 w-[24px]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
  <path d="M20 3.00001V7.00001M22 5.00001H18M4 17V19M5 18H3M9.937 15.5C9.84772 15.1539 9.66734 14.8381 9.41462 14.5854C9.1619 14.3327 8.84607 14.1523 8.5 14.063L2.365 12.481C2.26033 12.4513 2.16821 12.3883 2.10261 12.3014C2.03702 12.2146 2.00153 12.1088 2.00153 12C2.00153 11.8912 2.03702 11.7854 2.10261 11.6986C2.16821 11.6118 2.26033 11.5487 2.365 11.519L8.5 9.93601C8.84595 9.84681 9.16169 9.66658 9.4144 9.41404C9.66711 9.16151 9.84757 8.84589 9.937 8.50001L11.519 2.36501C11.5484 2.25992 11.6114 2.16735 11.6983 2.1014C11.7853 2.03545 11.8914 1.99976 12.0005 1.99976C12.1096 1.99976 12.2157 2.03545 12.3027 2.1014C12.3896 2.16735 12.4526 2.25992 12.482 2.36501L14.063 8.50001C14.1523 8.84608 14.3327 9.1619 14.5854 9.41462C14.8381 9.66734 15.1539 9.84773 15.5 9.93701L21.635 11.518C21.7405 11.5471 21.8335 11.61 21.8998 11.6971C21.9661 11.7841 22.0021 11.8906 22.0021 12C22.0021 12.1094 21.9661 12.2159 21.8998 12.3029C21.8335 12.39 21.7405 12.4529 21.635 12.482L15.5 14.063C15.1539 14.1523 14.8381 14.3327 14.5854 14.5854C14.3327 14.8381 14.1523 15.1539 14.063 15.5L12.481 21.635C12.4516 21.7401 12.3886 21.8327 12.3017 21.8986C12.2147 21.9646 12.1086 22.0003 11.9995 22.0003C11.8904 22.0003 11.7843 21.9646 11.6973 21.8986C11.6104 21.8327 11.5474 21.7401 11.518 21.635L9.937 15.5Z" stroke="black" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
</svg>
                </span>
                <span className="ml-0 font-medium">
                  {t('myPreferences')}
                </span>
              </span>
              <span
                onClick={() => setWhyNineMealsDrawerOpen(true)}
                className="inline-flex items-center
                    px-3 pl-3 py-1
                    text-sm font-medium
                    rounded-[10px]
                    border-[0.5px] border-[#F3F3F2]
                    bg-[rgba(255,255,255,0.64)]
                    shadow-[4px_12px_20px_0px_rgba(83,81,69,0.08)]
                    backdrop-blur-lg
                    cursor-pointer
                    md:hover:bg-[rgba(255,255,255,0.8)]
                    transition-colors"
              >
                <span className="ml-0 font-normal">{t('tips.whyNineMeals')}</span>
              </span>
            </div>
      </div>


      {/* Meal Sections */}
      <div
        className="min-h-0 mt-2 pt-0 md:pt-3 pb-32
                      bg-[#F8F8F7]
                      rounded-[12px_12px_0px_0px]
                      border-t-[2px]
                      border-t-[#FFF]
                      shadow-[0px_-4px_24px_0px_rgba(13,12,7,0.12)]"
      >
        {/* Mobile Meal Type Tabs - Only visible on mobile */}
        <div className="md:hidden px-6 pt-2 flex border-b border-[var(--Color-Grays-Gray-3,#E8E6E3)]">
          {availableTabs.map((mealType) => (
            <div
              key={mealType}
              onClick={() => setActiveMealTab(mealType)}
              className={`flex-1 py-3 text-center justify-center flex items-center border-b-[2px] cursor-pointer transition-colors ${
                activeMealTab === mealType
                  ? 'border-[#FFCB00] text-[var(--Color-Grays-Gray-10,#0D0C07)] font-bold'
                  : 'text-[var(--Color-Grays-Gray-7,#65635D)] font-normal border-[transparent]'
              }`}
            >
              <span className="flex items-center justify-center w-[20px] h-[20px]">
                {mealType === 'Breakfast' && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M8.33333 1.6665V3.33317M11.6667 1.6665V3.33317M13.3333 6.6665C13.5543 6.6665 13.7663 6.7543 13.9226 6.91058C14.0789 7.06686 14.1667 7.27882 14.1667 7.49984V14.1665C14.1667 15.0506 13.8155 15.8984 13.1904 16.5235C12.5652 17.1486 11.7174 17.4998 10.8333 17.4998H5.83333C4.94928 17.4998 4.10143 17.1486 3.47631 16.5235C2.85119 15.8984 2.5 15.0506 2.5 14.1665V7.49984C2.5 7.27882 2.5878 7.06686 2.74408 6.91058C2.90036 6.7543 3.11232 6.6665 3.33333 6.6665H15C15.8841 6.6665 16.7319 7.01769 17.357 7.64281C17.9821 8.26794 18.3333 9.11578 18.3333 9.99984C18.3333 10.8839 17.9821 11.7317 17.357 12.3569C16.7319 12.982 15.8841 13.3332 15 13.3332H14.1667M5 1.6665V3.33317" stroke={activeMealTab === 'Breakfast' ? '#0D0C07' : '#65635D'} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
                {mealType === 'Lunch' && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <g clipPath="url(#clip0_mobile_lunch)">
                      <path d="M13.3333 1.65967L11.4166 3.57633C10.9586 4.04366 10.702 4.67195 10.702 5.32633C10.702 5.98072 10.9586 6.60901 11.4166 7.07633L12.9166 8.57634C13.384 9.0344 14.0123 9.29098 14.6666 9.29098C15.321 9.29098 15.9493 9.0344 16.4166 8.57634L18.3333 6.65967M12.5 12.493L2.74997 2.743C2.41737 3.06888 2.15315 3.45785 1.97276 3.88713C1.79238 4.31641 1.69946 4.77736 1.69946 5.243C1.69946 5.70864 1.79238 6.1696 1.97276 6.59888C2.15315 7.02815 2.41737 7.41712 2.74997 7.743L8.83331 13.8263C9.41664 14.4097 10.5 14.4097 11.1666 13.8263L12.5 12.493ZM12.5 12.493L18.3333 18.3263M1.74997 18.1597L7.08331 12.9097M15.8333 4.15967L9.99997 9.993" stroke={activeMealTab === 'Lunch' ? '#0D0C07' : '#65635D'} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_mobile_lunch">
                        <rect width="20" height="20" fill="white" transform="translate(0 -0.00683594)"/>
                      </clipPath>
                    </defs>
                  </svg>
                )}
                {mealType === 'Dinner' && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                    <g clipPath="url(#clip0_mobile_dinner)">
                      <path d="M10.0001 3.48617V2.65283M10.0001 3.48617C8.45299 3.48617 6.96925 4.10075 5.87529 5.19471C4.78133 6.28867 4.16675 7.7724 4.16675 9.3195M10.0001 3.48617C11.5472 3.48617 13.0309 4.10075 14.1249 5.19471C15.2188 6.28867 15.8334 7.7724 15.8334 9.3195M12.8334 15.4862L15.5001 13.1528C15.6588 12.9996 15.8461 12.8792 16.0513 12.7983C16.2565 12.7175 16.4757 12.6779 16.6962 12.6818C16.9168 12.6857 17.1344 12.7329 17.3366 12.8209C17.5389 12.9089 17.7219 13.0358 17.8751 13.1945C18.0283 13.3532 18.1487 13.5405 18.2296 13.7457C18.3104 13.9509 18.35 14.1701 18.3461 14.3906C18.3423 14.6112 18.295 14.8288 18.207 15.0311C18.119 15.2333 17.9921 15.4163 17.8334 15.5695L14.8334 18.3195C14.2501 18.9862 13.4167 19.3195 12.5001 19.3195H9.16675C8.25008 19.3195 7.41675 18.9862 6.83342 18.3195L5.74842 17.0995C5.6703 17.0115 5.57444 16.9411 5.46715 16.8928C5.35986 16.8446 5.24356 16.8196 5.12592 16.8195H4.16675M1.66675 12.6528H11.6667C12.1088 12.6528 12.5327 12.8284 12.8453 13.141C13.1578 13.4535 13.3334 13.8775 13.3334 14.3195C13.3334 14.7615 13.1578 15.1854 12.8453 15.498C12.5327 15.8106 12.1088 15.9862 11.6667 15.9862H10.0001M3.33341 9.3195H16.6667M4.16675 12.6528V17.6528C4.16675 17.8738 4.07895 18.0858 3.92267 18.2421C3.76639 18.3984 3.55443 18.4862 3.33341 18.4862H1.66675" stroke={activeMealTab === 'Dinner' ? '#0D0C07' : '#65635D'} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_mobile_dinner">
                        <rect width="20" height="20" fill="white" transform="translate(0 0.986328)"/>
                      </clipPath>
                    </defs>
                  </svg>
                )}
              </span>
              <span className="ml-1.5 text-[14px] leading-[32px]">
                {t(`mealTypes.${mealType.toLowerCase()}`)}
              </span>
            </div>
          ))}
        </div>

        {/* Mobile: Vertical list of meals for active tab with slide animation */}
        <div className="md:hidden overflow-hidden">
          <div 
            className="flex transition-transform duration-300 ease-in-out"
            style={{
              transform: `translateX(-${availableTabs.indexOf(activeMealTab) * 100}%)`
            }}
          >
            {availableTabs.map((mealType) => {
              const meals = mealType === 'Breakfast' ? breakfasts : mealType === 'Lunch' ? lunches : dinners;
              
              // Sort meals by cooking priority
              const getCookingPriority = (recipe) => {
                const recipeId = recipe.id || recipe.title;
                const cookingState = cookingStates[recipeId];
                if (cookingState?.isCompleted) return 3;
                if (cookingState?.startedAt && !cookingState?.isCompleted) return 1;
                return 2;
              };
              const sortedMeals = [...meals].sort((a, b) => getCookingPriority(a) - getCookingPriority(b));

              return (
                <div 
                  key={mealType}
                  className="w-full flex-shrink-0 px-6 pt-4"
                >
                  {/* Meal count header 
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-[#878683] text-[12.8px] font-normal">
                      {t('mealCount', { count: meals.length })}{savedCookingStyle?.[mealType.toLowerCase()]?.cookFor2Days ? ` ${t('forTwoDays')}` : ''}
                    </span>
                  </div>*/}
                  
                  {/* Vertical list of recipe cards */}
                  <div className="flex flex-col gap-4 mt-3">
                    {sortedMeals.map((recipe, index) => renderMealCard(recipe, index, true))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Desktop: Original horizontal scroll sections */}
        <div className="hidden md:block">
          {breakfasts.length > 0 && renderMealSection('Breakfast', breakfasts)}
          {lunches.length > 0 && renderMealSection('Lunch', lunches)}
          {dinners.length > 0 && renderMealSection('Dinner', dinners)}
        </div>
        
        {/* Groceries List Card */}
        <div className="max-w-5xl mx-auto px-6 mt-4">
          <div
            className={`transition flex-1 min-w-[312px] p-5 overflow-hidden rounded-[12px] border-[0.5px] border-[var(--Color-Grays-Gray-2,#fff)] bg-[var(--Color-Grays-Gray-075,#EBEBEA)] flex flex-col
              ${
                productList.length === 0
                  ? "cursor-not-allowed opacity-60"
                  : "cursor-pointer md:hover:border-[var(--Color-Grays-Gray-0,#fff)]"
              }`}
            onClick={
              productList.length === 0
                ? undefined
                : () => navigate('/mymeals/groceries')
            }
          >
            <h3 className="text-lg font-semibold mb-2">{t('grocery.title')}</h3>
            <p className="text-gray-600 text-sm mb-4">
              {t('grocery.priceDisclaimer')}
            </p>
            {/* Tags */}
            <div className="flex gap-2 mb-6">
              {/* Grocery List Button */}
              <span
                className={`inline-flex items-center
              pl-2 pr-3 py-1
              text-sm font-medium
              rounded-[6px]
              border-[0.5px] border-[#F3F3F2]
              bg-[rgba(255,255,255,0.64)]
              shadow-[4px_12px_20px_0px_rgba(83,81,69,0.08)]
              backdrop-blur-lg`}
              >
                <span className="mr-0.5 w-[24px]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M9.9342 4.57344C10.3538 4.8132 10.4996 5.3477 10.2598 5.76728L7.67453 10.2915H9.48954C9.49731 10.2914 9.50505 10.2914 9.51278 10.2915H14.4874C14.4951 10.2914 14.5029 10.2914 14.5106 10.2915H16.3256L13.7404 5.76728C13.5006 5.3477 13.6464 4.8132 14.066 4.57344C14.4855 4.33369 15.02 4.47946 15.2598 4.89904L18.3412 10.2915H19.1477C19.1598 10.2912 19.172 10.2912 19.1843 10.2915H20.3334C20.8167 10.2915 21.2084 10.6832 21.2084 11.1665C21.2084 11.6497 20.8167 12.0415 20.3334 12.0415H19.8635L18.605 17.5197C18.4843 18.0977 18.1661 18.6158 17.7048 18.9849C17.2429 19.3544 16.6667 19.5511 16.0755 19.5415H13.6795C13.6717 19.5416 13.664 19.5416 13.6562 19.5415H10.344C10.3362 19.5416 10.3284 19.5416 10.3207 19.5415H7.92462C7.33342 19.5511 6.75724 19.3544 6.29538 18.9849C5.83245 18.6145 5.51359 18.0941 5.39387 17.5135L4.21072 12.0415H3.66675C3.1835 12.0415 2.79175 11.6497 2.79175 11.1665C2.79192 10.9946 3.18361 10.6027 3.66675 10.6027H4.89978C4.91169 10.2912 4.92358 10.2912 4.93542 10.2915H5.65897L8.74037 4.89904C8.98013 4.47946 9.51463 4.33369 9.9342 4.57344ZM6.00116 12.0415L6.43359 14.0415H8.93914L8.71692 12.0415H6.18212C6.17135 12.0417 6.16055 12.0417 6.14976 12.0415H6.00116ZM10.4777 12.0415L10.6999 14.0415H13.3003L13.5225 12.0415H10.4777ZM15.2832 12.0415L15.061 14.0415H17.6085L18.0679 12.0415H17.8504C17.8396 12.0417 17.8288 12.0417 17.818 12.0415H15.2832ZM17.2065 15.7915H14.8666L14.6444 17.7915H16.0834L16.1011 17.7917C16.2863 17.7954 16.4669 17.7341 16.6116 17.6184C16.7562 17.5026 16.8557 17.3398 16.8927 17.1583L16.8973 17.1372L17.2065 15.7915ZM12.8836 17.7915L13.1058 15.7915H10.8944L11.1166 17.7915H12.8836ZM9.35581 17.7915L9.13359 15.7915H6.81197L7.10532 17.1482L7.1075 17.1583L7.10744 17.1583C7.14445 17.3398 7.24394 17.5026 7.38859 17.6184C7.53325 17.7341 7.7139 17.7954 7.89911 17.7917L7.91675 17.7915H9.35581Z"
                      fill="#0D0C07"
                    />
                  </svg>
                </span>
                <span className="ml-0 font-normal">
                  <span className="font-semibold">{t('grocery.total')}</span>{" "}
                  {displayTotalCost || `0${userCountry ? getCountryCurrency(userCountry).symbol : '€'}`}
                </span>
              </span>
              <span
                className="inline-flex items-center
              px-2 pl-1 py-1
              text-sm font-medium
              rounded-[6px]
              border-[0.5px] border-[#F3F3F2]
              bg-[rgba(255,255,255,0.64)]
              shadow-[4px_12px_20px_0px_rgba(83,81,69,0.08)]
              backdrop-blur-lg"
              >
                <span className="text-[#FFCB00]">#</span>{" "}
                <span className="font-semibold">
                  {productList.reduce(
                    (total, category) =>
                      total + (category.items?.length || 0),
                    0
                  )}
                </span>{" "}
                &nbsp;{t('grocery.products')}
              </span>
            </div>
            {/* Grocery illustration */}
            <div
              className="flex w-full aspect-[3/2] sm:aspect-[3/1] rounded-[6px] mb-5 items-center justify-center relative bg-cover bg-top"
              style={{
                backgroundImage: `url(/assets/grocery-promo.jpg)`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            />

            <button
              className={`
                    inline-flex justify-center items-center gap-1
                    h-[48px] px-[24px] py-[14px]
                    rounded-[6px]
                    border-[0.5px] 
                    text-[#0D0C07] text-center
                    text-[14px] font-medium leading-[24px]
                    transition-colors
                    w-full
                    ${
                      productList.length === 0
                        ? "border-[#E5E5E5] bg-[#F5F5F5] cursor-not-allowed"
                        : "border-[#FFCB00] bg-[#FFCB00] md:hover:border-[#FFD429] md:hover:bg-[#FFD429]"
                    }
                  `}
              onClick={(e) => {
                e.stopPropagation();
                if (productList.length > 0) {
                  navigate('/mymeals/groceries');
                }
              }}
              disabled={productList.length === 0}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="25"
                viewBox="0 0 24 25"
                fill="none"
              >
                <path
                  d="M14.0662 4.88495C14.4595 4.66033 14.9539 4.77419 15.2117 5.13495L15.2595 5.21015L18.3416 10.6027H20.3337C20.8167 10.6029 21.2086 10.9947 21.2087 11.4777C21.2087 11.9609 20.8168 12.3526 20.3337 12.3527H19.863L18.6033 17.841L18.6013 17.84C18.4792 18.4143 18.1636 18.929 17.7048 19.2961C17.2451 19.6639 16.6721 19.8595 16.0837 19.8518V19.8527H7.91675V19.8518C7.32831 19.8595 6.75547 19.6639 6.29565 19.2961C5.83125 18.9246 5.51116 18.4023 5.39233 17.8195V17.8185L4.21069 12.3527H3.66675C3.1835 12.3527 2.79175 11.961 2.79175 11.4777C2.79192 10.9946 3.18361 10.6027 3.66675 10.6027H5.65894L8.73999 5.21015L8.78882 5.13495C9.04667 4.77426 9.54105 4.66022 9.93433 4.88495C10.3277 5.10977 10.4806 5.59389 10.3005 5.99921L10.2595 6.07929L7.67456 10.6027H16.3259L13.74 6.07929L13.7 5.99921C13.5198 5.59384 13.6727 5.10978 14.0662 4.88495ZM14.6453 18.1027H16.0837L16.1013 18.1037L16.239 18.0939C16.3745 18.0728 16.5028 18.0165 16.6111 17.9299C16.7557 17.8142 16.8553 17.6514 16.8923 17.4699L16.8972 17.4484L17.2068 16.1027H14.8669L14.6453 18.1027ZM7.10522 17.4601L7.10718 17.4699L7.14624 17.6018C7.19658 17.7297 7.27988 17.843 7.38843 17.9299C7.53308 18.0456 7.71396 18.1074 7.89917 18.1037C7.90503 18.1036 7.91089 18.1027 7.91675 18.1027H9.35522L9.13354 16.1027H6.81226L7.10522 17.4601ZM11.116 18.1027H12.8835L13.1052 16.1027H10.8943L11.116 18.1027ZM15.0613 14.3527H17.6082L18.0681 12.3527H15.283L15.0613 14.3527ZM10.7 14.3527H13.3005L13.5222 12.3527H10.4783L10.7 14.3527ZM6.43335 14.3527H8.93921L8.71753 12.3527H6.00171L6.43335 14.3527Z"
                  fill="#0D0C07"
                />
              </svg>
              <span>{t('actions.viewGroceriesList')}</span>
            </button>
          </div>
        </div>
      </div>

      

      {promoToast.visible && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-[90px] z-[260] px-4 py-2 rounded-[10px] shadow-[0px_8px_24px_rgba(13,12,7,0.18)] text-[13px] font-semibold"
          style={{
            backgroundColor:
              promoToast.kind === 'error'
                ? '#FEE2E2'
                : promoToast.kind === 'info'
                  ? '#DBEAFE'
                  : '#DCFCE7',
            color:
              promoToast.kind === 'error'
                ? '#991B1B'
                : promoToast.kind === 'info'
                  ? '#1E3A8A'
                  : '#166534',
          }}
        >
          {promoToast.message}
        </div>
      )}

      {/* Sticky Bottom Bar */}
      <MyMealsStickyFooter
        activeTab="meals"
        displayTotalCost={displayTotalCost}
        userCountry={userCountry}
        isPremium={isPremium}
        onStartNewMealPlan={handleCreateNewClick}
        onOpenStartOverDrawer={handleCreateNewClick}
        onOpenGroceries={() => navigate('/mymeals/groceries')}
        onOpenFavourites={() => navigate('/favorites')}
      />
      
      {/* Start Over Drawer - Uses TriesLeftDrawer */}
      <TriesLeftDrawer
        isOpen={startOverDrawerOpen}
        onClose={() => setStartOverDrawerOpen(false)}
        onTryAgain={handleStartOver}
        triesLeft={triesLeft}
        onTriesUpdate={onTriesUpdate}
      />

      {/* Start Over Confirmation Drawer (Premium users) */}
      <ReplaceMealPlanDrawer
        isOpen={startOverConfirmDrawerOpen}
        onClose={() => setStartOverConfirmDrawerOpen(false)}
        onConfirmReplace={() => {
          setStartOverConfirmDrawerOpen(false);
          handleStartOver();
        }}
        i18nPrefix="startOverMealPlan"
        isPremium={isPremium}
      />

      {/* Meal Plan Details Drawer */}
      <MealPlanDetailsDrawer
        isOpen={detailsDrawerOpen}
        onClose={() => setDetailsDrawerOpen(false)}
        onTryAgain={() => {
          setDetailsDrawerOpen(false);
          // Premium users see a confirmation drawer; free users see the tries-left drawer
          if (isPremium) {
            setStartOverConfirmDrawerOpen(true);
          } else {
            setStartOverDrawerOpen(true);
          }
        }}
        isPremium={isPremium}
        numberOfPeople={numberOfPeople}
        budget={budget}
        selectedCuisine={selectedCuisine}
        dietaryPreferences={dietaryPreferences}
      />

      {/* Why 9 Meals Drawer */}
      <WhyNineMealsDrawer
        isOpen={whyNineMealsDrawerOpen}
        onClose={() => setWhyNineMealsDrawerOpen(false)}
      />
    </div>
  );
};

export default MyMealPlans;